import React from "react";
import { cn } from "./cn";

const baseField =
  "w-full rounded-xl border bg-white text-gray-900 placeholder:text-gray-400 transition-all " +
  "focus:ring-2 focus:ring-indigo-500 focus:border-transparent " +
  "dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500 " +
  "disabled:opacity-60 disabled:cursor-not-allowed";

function fieldBorder(error) {
  return error
    ? "border-red-400 dark:border-red-500 focus:ring-red-500"
    : "border-gray-200 dark:border-gray-700";
}

/**
 * Labelled text input with optional leftIcon and error message.
 */
const Input = React.forwardRef(function Input(
  { label, error, leftIcon, rightIcon, hint, className, id, containerClassName, ...rest },
  ref
) {
  const inputId = id || rest.name;
  return (
    <div className={cn("space-y-1.5", containerClassName)}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">{leftIcon}</span>
        )}
        <input
          ref={ref}
          id={inputId}
          className={cn(baseField, fieldBorder(error), leftIcon ? "pl-10" : "pl-4", rightIcon ? "pr-11" : "pr-4", "py-3", className)}
          {...rest}
        />
        {rightIcon && <span className="absolute right-3 top-1/2 -translate-y-1/2">{rightIcon}</span>}
      </div>
      {error ? (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : hint ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  );
});

export const Textarea = React.forwardRef(function Textarea(
  { label, error, hint, className, id, rows = 4, ...rest },
  ref
) {
  const inputId = id || rest.name;
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        className={cn(baseField, fieldBorder(error), "px-4 py-3 resize-y", className)}
        {...rest}
      />
      {error ? (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : hint ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  );
});

export const Select = React.forwardRef(function Select(
  { label, error, hint, className, id, children, ...rest },
  ref
) {
  const inputId = id || rest.name;
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={inputId}
        className={cn(baseField, fieldBorder(error), "px-4 py-3", className)}
        {...rest}
      >
        {children}
      </select>
      {error ? (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : hint ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  );
});

export default Input;
